// The orb: colour follows the kit's state, size follows the mic or speaker level. Canvas, no
// framework, ported from live-voice-ai (including its battery-friendly idle frame rate).

import type { KitState } from "./protocol";

/** The kit's state, an error, or `paused`: a reply held where it stopped (protocol 1.9). */
export type OrbState = KitState | "error" | "paused";

export interface OrbTarget {
  hue: number;
  saturation: number;
  scale: number;
  /** 0 = steady, 1 = strong breathing. */
  pulse: number;
}

const BASE: Record<OrbState, Omit<OrbTarget, "scale">> = {
  idle: { hue: 212, saturation: 42, pulse: 0.15 },
  listening: { hue: 204, saturation: 92, pulse: 0.1 },
  thinking: { hue: 262, saturation: 78, pulse: 1 },
  speaking: { hue: 168, saturation: 72, pulse: 0 },
  paused: { hue: 168, saturation: 34, pulse: 0 },
  error: { hue: 4, saturation: 84, pulse: 0.4 },
};

/** The glass's radius, as a share of the canvas's side, at scale 1. */
export const ORB_GLASS = 0.3;
/** How much the glass swells with a loud voice (scale up to 1 + this). */
export const ORB_SWELL = 0.35;
/** How much it breathes, times its state's `pulse`. */
export const ORB_BREATHE = 0.03;

export function orbTarget(state: OrbState, level: number): OrbTarget {
  const reactive = state === "listening" || state === "speaking";
  const scale = state === "thinking" ? 1.04 : 1 + (reactive ? Math.min(1, level) * ORB_SWELL : 0);
  return { ...BASE[state], scale };
}

/**
 * The largest the glass is ever drawn, as a share of the canvas's side (a radius): its loudest
 * swell at the top of a breath, in any state. What a tap area over the orb has to cover.
 */
export function orbMaxRadius(): number {
  const states = Object.keys(BASE) as OrbState[];
  return ORB_GLASS * Math.max(...states.map((s) => orbTarget(s, 1).scale * (1 + ORB_BREATHE * BASE[s].pulse)));
}

/** Fast attack, slow release. */
export function smoothLevel(prev: number, input: number): number {
  const k = input > prev ? 0.6 : 0.12;
  return prev + (input - prev) * k;
}

/** The line under the orb: what's happening and what you can do next. On touch, speaking is just
 * "Speaking" (a tap pauses it; the label doesn't say so). A tap or click on a speaking orb pauses
 * the reply and the next one carries on; talking over it still interrupts. */
export function orbCaption(state: OrbState, surface: "touch" | "pointer" = "pointer"): string {
  switch (state) {
    case "idle":
      return surface === "touch" ? "Tap to talk" : "Click to talk";
    case "listening":
      return "Listening…";
    case "thinking":
      return "Thinking…";
    case "speaking":
      return surface === "touch" ? "Speaking" : "Speaking · click to pause, or talk over it";
    case "paused":
      return surface === "touch" ? "Paused · tap to continue" : "Paused · click to continue";
    default:
      return "";
  }
}

export class Orb {
  private state: OrbState = "idle";
  private level = 0;
  private hue = 220;
  private sat = 12;
  private scale = 1;
  private raf = 0;
  private readonly ctx: CanvasRenderingContext2D;
  /** prefers-reduced-motion: no breathing and no wobble (colour and level still show). */
  private readonly reducedMotion: MediaQueryList | null =
    typeof matchMedia === "function" ? matchMedia("(prefers-reduced-motion: reduce)") : null;

  /**
   * `restWhenIdle`: the idle orb is still (no breathing, no wobble), so motion means the mic or
   * the speaker is live. The phone page uses it.
   */
  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly levelSource: () => number,
    private readonly opts: { restWhenIdle?: boolean } = {},
  ) {
    this.ctx = canvas.getContext("2d")!;
    let last = 0;
    const frame = (t: number) => {
      // Idle: a slow breathe looks the same at ~12 fps and costs a fraction of the battery.
      if (this.state !== "idle" || t - last > 80) {
        last = t;
        this.draw(t / 1000);
      }
      this.raf = requestAnimationFrame(frame);
    };
    this.raf = requestAnimationFrame(frame);
  }

  setState(state: OrbState): void {
    this.state = state;
    this.canvas.dataset.state = state;
  }

  destroy(): void {
    cancelAnimationFrame(this.raf);
  }

  /** The fluid's phase: it only moves while something is happening (and never under reduced motion). */
  private phase = 0;
  private lastT = 0;

  /**
   * A glass sphere with coloured fluid inside: a clear body that tints with the state, three
   * soft currents of colour turning inside it (faster while thinking, stirred by the voice), a
   * darker refracted rim, a caustic where light pools at the bottom, and a specular highlight
   * and rim light on top. About eight gradient fills a frame on a ~400px canvas.
   */
  private draw(t: number): void {
    const { canvas, ctx } = this;
    const dpr = window.devicePixelRatio || 1;
    const size = Math.max(1, Math.round(canvas.clientWidth * dpr));
    if (canvas.width !== size) canvas.width = canvas.height = size;
    this.level = smoothLevel(this.level, this.levelSource());
    const target = orbTarget(this.state, this.level);
    const ease = 0.12;
    this.hue += (target.hue - this.hue) * ease;
    this.sat += (target.saturation - this.sat) * ease;
    const still =
      (this.reducedMotion?.matches ?? false) || this.state === "paused" || (!!this.opts.restWhenIdle && this.state === "idle");
    const breathe = still ? 1 : 1 + Math.sin(t * 2.4) * ORB_BREATHE * target.pulse;
    this.scale += (target.scale * breathe - this.scale) * 0.25;
    const dt = Math.min(0.1, Math.max(0, t - this.lastT));
    this.lastT = t;
    if (!still) this.phase += dt * (0.35 + this.level * 2.2 + (this.state === "thinking" ? 1.4 : 0));

    const h = this.hue;
    const s = this.sat;
    const hsla = (dh: number, sat: number, l: number, a: number) => `hsl(${h + dh} ${sat}% ${l}% / ${a})`;
    const p = this.phase;
    ctx.clearRect(0, 0, size, size);
    const c = size / 2;
    const r = size * ORB_GLASS * this.scale;

    // the coloured light it throws on what is around it
    const halo = ctx.createRadialGradient(c, c, r * 0.55, c, c, Math.min(r * 1.6, size * 0.5));
    halo.addColorStop(0, hsla(0, s, 60, 0.32 + this.level * 0.25));
    halo.addColorStop(1, hsla(0, s, 60, 0));
    ctx.fillStyle = halo;
    ctx.fillRect(0, 0, size, size);

    // the sphere's outline: round at rest, a slight living wobble when not
    ctx.save();
    ctx.translate(c, c);
    const outline = new Path2D();
    const points = 72;
    for (let i = 0; i <= points; i++) {
      const a = (i / points) * Math.PI * 2;
      const wobble = still ? 1 : 1 + 0.005 * Math.sin(a * 3 + p * 1.3) + 0.006 * Math.sin(a * 5 - p * 1.9) * (0.4 + this.level);
      const x = Math.cos(a) * r * wobble;
      const y = Math.sin(a) * r * wobble;
      if (i) outline.lineTo(x, y);
      else outline.moveTo(x, y);
    }
    outline.closePath();

    ctx.save();
    ctx.clip(outline);
    // clear glass body, lightly tinted
    const glass = ctx.createRadialGradient(-r * 0.25, -r * 0.3, 0, 0, 0, r);
    glass.addColorStop(0, hsla(10, s * 0.5, 97, 0.7));
    glass.addColorStop(0.65, hsla(0, s, 78, 0.45));
    glass.addColorStop(1, hsla(-10, s, 52, 0.6));
    ctx.fillStyle = glass;
    ctx.fillRect(-r * 1.2, -r * 1.2, r * 2.4, r * 2.4);
    // currents of colour inside
    const currents: Array<[number, number, number]> = [
      [-28, 1, 0],
      [22, -1.3, 2.1],
      [55, 0.8, 4.2],
    ];
    for (const [dh, dir, off] of currents) {
      const a = p * dir + off;
      const cx = Math.cos(a) * r * 0.38;
      const cy = Math.sin(a * 1.2) * r * 0.3 + r * 0.12;
      const br = r * (0.62 + 0.08 * Math.sin(p * 1.1 + off)) * (1 + this.level * 0.35);
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, br);
      g.addColorStop(0, hsla(dh, s, 56, 0.8));
      g.addColorStop(0.55, hsla(dh, s, 58, 0.35));
      g.addColorStop(1, hsla(dh, s, 60, 0));
      ctx.fillStyle = g;
      ctx.fillRect(-r * 1.2, -r * 1.2, r * 2.4, r * 2.4);
    }
    // refraction: the glass thickens and darkens towards the edge
    const rim = ctx.createRadialGradient(0, 0, r * 0.62, 0, 0, r * 1.02);
    rim.addColorStop(0, hsla(0, s, 30, 0));
    rim.addColorStop(1, hsla(-15, s, 26, 0.38));
    ctx.fillStyle = rim;
    ctx.fillRect(-r * 1.2, -r * 1.2, r * 2.4, r * 2.4);
    // caustic: light gathered at the bottom
    const caustic = ctx.createRadialGradient(0, r * 0.78, 0, 0, r * 0.78, r * 0.7);
    caustic.addColorStop(0, hsla(20, s, 92, 0.75));
    caustic.addColorStop(1, hsla(20, s, 92, 0));
    ctx.fillStyle = caustic;
    ctx.fillRect(-r * 1.2, -r * 1.2, r * 2.4, r * 2.4);
    ctx.restore();

    // specular highlight
    ctx.save();
    ctx.translate(-r * 0.3, -r * 0.47);
    ctx.rotate(-0.42);
    ctx.scale(1, 0.55);
    const spec = ctx.createRadialGradient(0, 0, 0, 0, 0, r * 0.48);
    spec.addColorStop(0, "rgba(255,255,255,0.92)");
    spec.addColorStop(0.5, "rgba(255,255,255,0.35)");
    spec.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = spec;
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.48, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // rim light: bright where the light comes in (top) and where it leaves (bottom)
    const edge = ctx.createLinearGradient(0, -r, 0, r);
    edge.addColorStop(0, "rgba(255,255,255,0.95)");
    edge.addColorStop(0.35, "rgba(255,255,255,0.12)");
    edge.addColorStop(0.7, "rgba(255,255,255,0.08)");
    edge.addColorStop(1, "rgba(255,255,255,0.6)");
    ctx.strokeStyle = edge;
    ctx.lineWidth = Math.max(1, size * 0.007);
    ctx.stroke(outline);
    if (this.state === "paused") drawPauseGlyph(ctx, r);
    ctx.restore();
  }
}

/** A pause sign (two rounded bars) in the middle of the orb: paused, not stopped. */
function drawPauseGlyph(ctx: CanvasRenderingContext2D, r: number): void {
  const w = r * 0.13;
  const h = r * 0.5;
  const gap = r * 0.11;
  ctx.save();
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.shadowColor = "rgba(0,0,0,0.28)";
  ctx.shadowBlur = r * 0.08;
  for (const x of [-gap / 2 - w, gap / 2]) {
    ctx.beginPath();
    const y = -h / 2;
    const rad = w / 2;
    ctx.moveTo(x + rad, y);
    ctx.arcTo(x + w, y, x + w, y + h, rad);
    ctx.arcTo(x + w, y + h, x, y + h, rad);
    ctx.arcTo(x, y + h, x, y, rad);
    ctx.arcTo(x, y, x + w, y, rad);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}
